'use client';

import Image from 'next/image';
import {useTranslations} from 'next-intl';
import {useRouter} from '#i18n/navigation.ts';
import {ROUTES} from '#utils/routes.ts';
import LanguagePicker from '#components/LanguagePicker/LanguagePicker.tsx';
import Logo from '@purpose/ui/assets/logo/black.svg';
import {useAuthForm} from '#contexts/AuthFormContext';
import {useUser} from '#contexts/UserContext';
import {getAuthRedirectRoute} from '#utils/helpers';
import {checkUserRole} from '#utils/cookies';
import {USER_ROLES} from '#utils/constants';
import {Loader} from '@purpose/ui/components/Loader';
import React, {useState, useEffect} from 'react';
import WithWelcomeImage from './(auth)/_components/wrappers/WithWelcomeImage';
import BottomButtonsWithMobileGradient from './(auth)/_components/molecules/BottomButtonsWithMobileGradient';
import {getTypographyVariantSx} from '@purpose/ui/styles/theme/getTypographyVariantSx.ts';
import {useResponsive} from '@purpose/ui/hooks/useResponsive';

function Page() {
  const t = useTranslations('WelcomePage');

  const {breakpoint} = useResponsive();

  const router = useRouter();
  const {resetAll} = useAuthForm();
  const {userRole: userRoleInStore} = useUser();

  const [isSignUpLoading, setIsSignUpLoading] = useState(false);
  const [isSignInLoading, setIsSignInLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      await new Promise(resolve => setTimeout(resolve, 100));

      const role = checkUserRole();

      if (role) {
        const redirectPath = getAuthRedirectRoute(role);
        router.push({
          pathname: redirectPath
        });
      } else if (userRoleInStore) {
        const redirectPath = getAuthRedirectRoute(
          userRoleInStore as USER_ROLES
        );
        router.push({
          pathname: redirectPath
        });
      } else {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, [userRoleInStore, router]);

  const handleBeginJourney = () => {
    setIsSignUpLoading(true);
    resetAll();
    router.push(ROUTES.accountType);
  };

  const handleAlreadyUser = () => {
    setIsSignInLoading(true);
    router.push(ROUTES.signIn);
  };

  if (isCheckingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader sx={{height: '100vh'}} />
      </div>
    );
  }

  return (
    <WithWelcomeImage containerClassname="justify-end gap-[208px] sm:gap-[214px] md:justify-center md:gap-6">
      <div className="flex flex-col items-center justify-center gap-10 sm:gap-6">
        <Image src={Logo} alt="Logo" width={108} />

        <div className="flex flex-col items-center justify-center gap-2.5">
          <h1
            style={{
              ...getTypographyVariantSx({
                xs: 'h2',
                sm: 'h2',
                md: 'h1',
                lg: 'h1',
                xl: 'h1'
              })[breakpoint]
            }}
            className="text-primary-base text-center"
          >
            {t('title')}
          </h1>
          <p
            className="text-secondary-90 text-center whitespace-pre-line"
            style={{
              ...getTypographyVariantSx({
                xs: 'body1',
                sm: 'body2',
                md: 'body2',
                lg: 'body2',
                xl: 'body2'
              })[breakpoint]
            }}
          >
            {t('subtitle')}
          </p>
        </div>
      </div>

      <BottomButtonsWithMobileGradient
        topButtonProps={{
          isLoading: isSignUpLoading,
          title: t('mainBtn'),
          onClick: handleBeginJourney
        }}
        bottomButtonProps={{
          title: t('secondaryBtn'),
          onClick: handleAlreadyUser,
          isLoading: isSignInLoading
        }}
      />
      <div className="absolute top-6 right-6 z-1 md:top-8 md:right-8 xl:right-14">
        <LanguagePicker />
      </div>
    </WithWelcomeImage>
  );
}

export default Page;
